/**
 * Parse timestamp from UTC (as stored in MongoDB) to Date object
 * MongoDB stores dates as UTC, so timestamps come as UTC strings with 'Z' suffix
 * We parse them as UTC and let JavaScript convert to local timezone for display
 * @param {*} timestamp - Timestamp in various formats (Date, string, number, MongoDB $date object)
 * @returns {Date|null} - Parsed Date object or null if invalid
 */
export const parseTimestamp = timestamp => {
  if (!timestamp) return null;
  
  // If it's already a Date object, use it directly
  if (timestamp instanceof Date) {
    return timestamp;
  }
  
  // Handle MongoDB $date format
  if (typeof timestamp === "object" && timestamp !== null) {
    if (timestamp.$date) {
      timestamp = timestamp.$date;
    } else if (timestamp.toString) {
      timestamp = timestamp.toString();
    }
  }
  
  // If it's a string, parse it
  if (typeof timestamp === "string") {
    // If it has 'Z' suffix, it's UTC - parse it directly
    // JavaScript's Date will handle the UTC to local timezone conversion
    if (timestamp.endsWith("Z") || timestamp.includes("+") || timestamp.match(/[+-]\d{2}:\d{2}$/)) {
      return new Date(timestamp);
    }
    
    // If no timezone indicator, assume it's UTC and append 'Z'
    return new Date(timestamp + "Z");
  }
  
  // Handle numeric timestamps
  if (typeof timestamp === "number" || (typeof timestamp === "string" && /^\d+$/.test(timestamp))) {
    return new Date(typeof timestamp === "string" ? parseInt(timestamp, 10) : timestamp);
  }
  
  return new Date(timestamp);
};

/**
 * Format a timestamp (number, ISO string, or Mongo $date) into a human-readable string
 * Properly handles UTC timestamps from MongoDB and converts to local timezone
 * @param {*} ts - Timestamp in various formats
 * @param {string} fallback - Text to return if timestamp is invalid (default: "Never")
 * @returns {string} - Formatted timestamp string
 */
export const formatTimestamp = (ts, fallback = "Never") => {
  if (!ts && ts !== 0) return fallback;
  
  const date = parseTimestamp(ts);
  if (!date || isNaN(date.getTime())) return fallback === "Never" ? "Invalid date" : fallback;
  
  // toLocaleString automatically converts from UTC (stored in DB) to user's local timezone
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true
  });
};

/**
 * Format time for timeline (just time portion)
 * Timestamps are stored in MongoDB as UTC, converted to user's local timezone for display
 * @param {*} timestamp - Timestamp in various formats
 * @param {string} fallback - Text to return if timestamp is invalid (default: "Unknown time")
 * @param {boolean} includeSeconds - Whether to include seconds in the output (default: false)
 * @returns {string} - Formatted time string
 */
export const formatTime = (timestamp, fallback = "Unknown time", includeSeconds = false) => {
  if (!timestamp) return fallback;
  
  const date = parseTimestamp(timestamp);
  if (!date || isNaN(date.getTime())) return fallback;
  
  // toLocaleTimeString automatically converts from UTC (stored in DB) to user's local timezone
  const options = {
    hour: "numeric",
    minute: "2-digit",
    hour12: true
  };
  
  if (includeSeconds) {
    options.second = "2-digit";
  }
  
  return date.toLocaleTimeString("en-US", options);
};

